<template>
	<div class="pincode">
		<ol class="pincode__container">
			<li
				v-for="(field, index) in fields"
				:key="index"
				class="pincode__container__input-box"
				:class="{'pincode__container__input-box--error': showErrorState}"
			>
			<input
				@keydown="inputKeyPress($event, index)"
				@click="inputSelected($event)"
				@paste="paste($event, index)"
				@input="handleInput(index, $event)"
				v-model.trim="field.value"
				type="text"
				autocomplete="off"
				:disabled="disabled"
				:ref="el => { if (el) inputRefs[index] = el as HTMLInputElement }"
				:maxlength="maxLength"
			>
			</li>
		</ol>
	</div>
</template>

<script lang='ts'>
import { defineComponent } from 'vue'

type PinCode = {
	value: string;
}

export default defineComponent({
	name: 'PinCode',
	props: {
		numberOfFields: {
			type: Number,
			default: 1
		},
		maxLength: {
			type: Number,
			default: 1
		},
		disabled: {
			type: Boolean,
			default: false
		},
		pin: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			fields: [] as PinCode[],
			showErrorState: false,
			inputRefs: [] as HTMLInputElement[]
		}
	},
	computed: {
		computedPinCode(): string {
			return this.fields.map(field => field.value).join('')
		}
	},
	methods: {
		showError() {
			this.showErrorState = true
		},
		isUndefined(val: string) {
			return val === '' || val === undefined || val === null
		},
		paste(clipboardEvent: ClipboardEvent, index: number) {
			if (clipboardEvent.clipboardData) {
				const text = clipboardEvent.clipboardData.getData('text').trim().split('')

				this.setPinCode(text)
				const maxFields = this.fields.length
				const max = index + text.length
				const moveTo = max >= maxFields ? maxFields : max
				this.moveToInput(moveTo)
			}
		},
		setPinCode(pin: string | string[], index = 0) {
			const pinArray = Array.isArray(pin) ? pin : pin.split('')
			this.fields.forEach((field, indx) => {
				if (indx >= index && pinArray[indx] !== undefined) {
					field.value = pinArray[indx]
				}
			})
		},
		clearPincode() {
			this.fields.forEach((field) => {
				field.value = ''
			})
		},
		inputKeyPress(evt: KeyboardEvent, index: number) {
			if (evt.code !== 'Backspace') return

			const input = evt.target as HTMLInputElement

			if (input !== undefined && this.isUndefined(input.value)) this.moveToInput(index - 1)
		},
		inputSelected(event: MouseEvent) {
			const input = event.target as HTMLInputElement
			if (input && this.isUndefined(input.value)) {
				this.moveToInput()
			}
		},
		handleInput(index: number, event: Event) {
			const input = event.target as HTMLInputElement
			const value = input.value.trim()
			
			// If a character was entered, move to next field
			if (value.length > 0 && value.length <= this.maxLength) {
				this.$nextTick(() => {
					// Move to next field if not at the end
					if (index < this.fields.length - 1) {
						this.moveToInput(index + 1)
					}
				})
			}
		},
		moveToInput(goto = undefined as undefined | number) {
			let index: number
			if (goto !== undefined) {
				index = goto
			} else {
				// Find first empty field
				index = this.fields.findIndex(field => this.isUndefined(field.value))
			}
			
			// Clamp index to valid range
			if (index < 0 || index >= this.fields.length) {
				return false
			}
			
			const input = this.inputRefs[index]
			if (input && typeof input.focus === 'function') {
				input.focus()
				return true
			}
			return false
		},
		pinComplete() {
			this.$emit('pincode', 'complete', this.computedPinCode.toLowerCase())
		}
	},
	watch: {
		pin: {
			handler(pin: string) {
				this.$nextTick(() => this.setPinCode(pin))
			},
			immediate: true
		},
		numberOfFields: {
			handler(val: number) {
				this.fields = Array(val).fill(null).map(_item => ({ value: '' })) as PinCode[]
				// Reset refs array when fields change
				this.inputRefs = []
			},
			immediate: true
		},
		computedPinCode(pin: string) {
			this.showErrorState = false
			if (pin.length >= this.numberOfFields) this.pinComplete()
		}
	}
})
</script>

<style lang="scss">
.pincode {
	h3 {
		margin-top: 25px;
	}

	&__container {
		display: flex;
		gap: 20px;
		list-style-type: none;
		margin: 0;
		padding: 0;

		&__input-box {
			position: relative;
			width: 100%;

			transition: all 0.4s;

			&--error {
				box-shadow: 0 0 5px $color-red;
			}

			&::before {
				content: "";
				display: block;
				height: 0;
				padding-bottom: 100%;
				opacity: 0;
			}

			input {
				box-sizing: border-box;
				position: absolute;
				top: 0;
				right: 0;
				bottom: 0;
				left: 0;
				width: 100%;
				height: 100%;
				text-align: center;
				font-size: 30px;
				text-transform: uppercase;
			}
		}

	}
}
</style>
